/**
 * Surggies Audio Engine
 *
 * Pure WebAudio synthesized SFX + sequenced 8-bit BGM with crossfade.
 * No external audio files. SSR-safe (all calls are no-ops on the server).
 *
 * Usage:
 *   import { audio, playSfx, playBgm } from '@/engine/audio';
 *   // After a user gesture:
 *   await audio.init();
 *   playSfx('menu_select');
 *   playBgm('overworld');
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type SfxKey =
  | 'menu_move'
  | 'menu_select'
  | 'menu_back'
  | 'menu_invalid'
  | 'battle_hit'
  | 'battle_super'
  | 'battle_weak'
  | 'battle_faint'
  | 'battle_win'
  | 'battle_lose'
  | 'catch_throw'
  | 'catch_shake'
  | 'catch_success'
  | 'catch_fail'
  | 'level_up'
  | 'evolution'
  | 'heal'
  | 'door_open'
  | 'item_pickup'
  | 'menu_open'
  | 'menu_close'
  | 'wild_appear'
  | 'trainer_spotted';

export type BgmKey =
  | 'title'
  | 'overworld'
  | 'route'
  | 'town'
  | 'cave'
  | 'battle_wild'
  | 'battle_trainer'
  | 'victory';

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

type OscType = 'sine' | 'square' | 'triangle' | 'sawtooth';

interface ToneStep {
  /** Frequency in Hz. 0 = rest. */
  freq: number;
  /** Duration in seconds. */
  dur: number;
  /** Oscillator type. */
  type?: OscType;
  /** Peak gain 0..1. */
  gain?: number;
  /** Optional pitch sweep destination Hz. */
  sweepTo?: number;
}

interface BgmTrack {
  /** Loop tempo: seconds per step. */
  step: number;
  /** Lead voice sequence (one entry per step; 0 = rest). */
  lead: number[];
  /** Bass voice sequence. */
  bass: number[];
  /** Optional arp/harmony voice. */
  arp?: number[];
  /** Lead oscillator type. */
  leadType?: OscType;
  /** Bass oscillator type. */
  bassType?: OscType;
  /** Arp oscillator type. */
  arpType?: OscType;
}

interface ActiveBgm {
  key: BgmKey;
  gain: GainNode;
  timer: number;
  // We keep the scheduler alive via setInterval; oscillators are short-lived per note.
}

// ---------------------------------------------------------------------------
// SFX definitions — retro chiptune envelopes
// ---------------------------------------------------------------------------

const SFX_DEFS: Record<SfxKey, ToneStep[]> = {
  menu_move: [{ freq: 220, dur: 0.08, type: 'square', gain: 0.25 }],
  menu_select: [
    { freq: 440, dur: 0.06, type: 'square', gain: 0.3, sweepTo: 880 },
    { freq: 880, dur: 0.06, type: 'square', gain: 0.25 },
  ],
  menu_back: [
    { freq: 440, dur: 0.06, type: 'square', gain: 0.25, sweepTo: 220 },
  ],
  menu_invalid: [
    { freq: 160, dur: 0.08, type: 'square', gain: 0.3 },
    { freq: 110, dur: 0.12, type: 'square', gain: 0.3 },
  ],

  battle_hit: [{ freq: 0, dur: 0.1, type: 'square', gain: 0.4 }], // noise burst handled specially
  battle_super: [
    { freq: 220, dur: 0.06, type: 'sawtooth', gain: 0.4, sweepTo: 880 },
    { freq: 880, dur: 0.12, type: 'sawtooth', gain: 0.35 },
  ],
  battle_weak: [{ freq: 180, dur: 0.18, type: 'triangle', gain: 0.25 }],
  battle_faint: [
    { freq: 440, dur: 0.1, type: 'sawtooth', gain: 0.3, sweepTo: 110 },
    { freq: 110, dur: 0.25, type: 'sawtooth', gain: 0.25 },
  ],
  battle_win: [
    { freq: 523, dur: 0.12, type: 'square', gain: 0.3 }, // C
    { freq: 659, dur: 0.12, type: 'square', gain: 0.3 }, // E
    { freq: 784, dur: 0.12, type: 'square', gain: 0.3 }, // G
    { freq: 1047, dur: 0.24, type: 'square', gain: 0.35 }, // C
  ],
  battle_lose: [
    { freq: 392, dur: 0.18, type: 'triangle', gain: 0.3 }, // G
    { freq: 349, dur: 0.18, type: 'triangle', gain: 0.3 }, // F
    { freq: 294, dur: 0.36, type: 'triangle', gain: 0.3 }, // D
  ],

  catch_throw: [
    { freq: 220, dur: 0.05, type: 'square', gain: 0.3, sweepTo: 660 },
  ],
  catch_shake: [
    { freq: 880, dur: 0.04, type: 'square', gain: 0.25 },
    { freq: 0, dur: 0.06, type: 'square', gain: 0 },
    { freq: 880, dur: 0.04, type: 'square', gain: 0.25 },
  ],
  catch_success: [
    { freq: 523, dur: 0.12, type: 'square', gain: 0.3 },
    { freq: 659, dur: 0.12, type: 'square', gain: 0.3 },
    { freq: 784, dur: 0.12, type: 'square', gain: 0.3 },
    { freq: 1047, dur: 0.12, type: 'square', gain: 0.3 },
    { freq: 1319, dur: 0.32, type: 'square', gain: 0.35 },
  ],
  catch_fail: [
    { freq: 440, dur: 0.1, type: 'square', gain: 0.3, sweepTo: 165 },
  ],

  level_up: [
    { freq: 523, dur: 0.1, type: 'square', gain: 0.3 }, // C
    { freq: 659, dur: 0.1, type: 'square', gain: 0.3 }, // E
    { freq: 784, dur: 0.1, type: 'square', gain: 0.3 }, // G
    { freq: 1047, dur: 0.3, type: 'square', gain: 0.35 }, // C major chord arpeggio
  ],
  evolution: [
    { freq: 330, dur: 0.08, type: 'sawtooth', gain: 0.3, sweepTo: 1320 },
    { freq: 1320, dur: 0.08, type: 'sawtooth', gain: 0.3, sweepTo: 330 },
    { freq: 330, dur: 0.08, type: 'sawtooth', gain: 0.3, sweepTo: 1320 },
    { freq: 1320, dur: 0.3, type: 'square', gain: 0.35 },
  ],
  heal: [
    { freq: 523, dur: 0.08, type: 'sine', gain: 0.3 },
    { freq: 784, dur: 0.08, type: 'sine', gain: 0.3 },
    { freq: 1047, dur: 0.2, type: 'sine', gain: 0.3 },
  ],

  door_open: [
    { freq: 200, dur: 0.06, type: 'square', gain: 0.25, sweepTo: 100 },
  ],
  item_pickup: [
    { freq: 880, dur: 0.06, type: 'square', gain: 0.3 },
    { freq: 1319, dur: 0.1, type: 'square', gain: 0.3 },
  ],
  menu_open: [
    { freq: 660, dur: 0.05, type: 'square', gain: 0.25 },
    { freq: 880, dur: 0.05, type: 'square', gain: 0.25 },
  ],
  menu_close: [
    { freq: 880, dur: 0.05, type: 'square', gain: 0.25 },
    { freq: 660, dur: 0.05, type: 'square', gain: 0.25 },
  ],
  wild_appear: [
    { freq: 110, dur: 0.08, type: 'sawtooth', gain: 0.35, sweepTo: 440 },
    { freq: 440, dur: 0.08, type: 'sawtooth', gain: 0.35, sweepTo: 110 },
    { freq: 110, dur: 0.16, type: 'sawtooth', gain: 0.3 },
  ],
  trainer_spotted: [
    { freq: 440, dur: 0.1, type: 'square', gain: 0.35 },
    { freq: 587, dur: 0.1, type: 'square', gain: 0.35 },
    { freq: 740, dur: 0.2, type: 'square', gain: 0.35 },
  ],
};

/** SFX keys that use noise (random) bursts instead of pure tones. */
const NOISE_SFX: Partial<Record<SfxKey, { dur: number; gain: number }>> = {
  battle_hit: { dur: 0.1, gain: 0.4 },
};

// ---------------------------------------------------------------------------
// BGM definitions — 8-bit sequenced loops
// ---------------------------------------------------------------------------

// Note helpers (equal temperament)
const N = {
  // Octave 3
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0, B3: 246.94,
  // Octave 4
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
  // Octave 5
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0, B5: 987.77,
  // Octave 6
  C6: 1046.5,
  REST: 0,
};

const BGM_DEFS: Record<BgmKey, BgmTrack> = {
  title: {
    step: 0.32,
    lead: [N.C5, N.E5, N.G5, N.C6, N.G5, N.E5, N.C5, N.REST],
    bass: [N.C3, N.C3, N.G3, N.G3, N.C3, N.C3, N.G3, N.REST],
    leadType: 'square',
    bassType: 'triangle',
  },
  overworld: {
    // Cheerful C major 8-note loop
    step: 0.18,
    lead: [N.C5, N.E5, N.G5, N.E5, N.F5, N.A5, N.G5, N.E5],
    bass: [N.C3, N.G3, N.C3, N.G3, N.F3, N.C3, N.G3, N.C3],
    arp:  [N.G4, N.REST, N.E4, N.REST, N.A4, N.REST, N.G4, N.REST],
    leadType: 'square',
    bassType: 'triangle',
    arpType: 'square',
  },
  route: {
    step: 0.16,
    lead: [N.E5, N.G5, N.A5, N.G5, N.E5, N.D5, N.C5, N.D5],
    bass: [N.A3, N.A3, N.E3, N.E3, N.F3, N.F3, N.G3, N.G3],
    leadType: 'square',
    bassType: 'triangle',
  },
  town: {
    step: 0.22,
    lead: [N.G4, N.A4, N.B4, N.C5, N.D5, N.C5, N.B4, N.A4],
    bass: [N.C3, N.C3, N.G3, N.G3, N.A3, N.A3, N.F3, N.G3],
    leadType: 'triangle',
    bassType: 'triangle',
  },
  cave: {
    // Low, sparse, ominous
    step: 0.4,
    lead: [N.A3, N.REST, N.C4, N.REST, N.E4, N.REST, N.D4, N.REST],
    bass: [N.A3, N.A3, N.A3, N.E3, N.F3, N.F3, N.E3, N.E3],
    leadType: 'triangle',
    bassType: 'sawtooth',
  },
  battle_wild: {
    // Tense A minor 4-note loop (expanded to 8 for variation)
    step: 0.13,
    lead: [N.A4, N.C5, N.E5, N.A5, N.G5, N.E5, N.C5, N.A4],
    bass: [N.A3, N.A3, N.E3, N.E3, N.F3, N.F3, N.G3, N.G3],
    arp:  [N.E5, N.A5, N.E5, N.A5, N.D5, N.G5, N.D5, N.G5],
    leadType: 'square',
    bassType: 'sawtooth',
    arpType: 'square',
  },
  battle_trainer: {
    // Driving D minor
    step: 0.12,
    lead: [N.D5, N.F5, N.A5, N.D5, N.C5, N.A4, N.F4, N.A4],
    bass: [N.D3, N.D3, N.D3, N.A3, N.G3, N.G3, N.D3, N.A3],
    arp:  [N.A4, N.D5, N.F5, N.A5, N.G4, N.C5, N.E5, N.G5],
    leadType: 'square',
    bassType: 'sawtooth',
    arpType: 'square',
  },
  victory: {
    step: 0.16,
    lead: [N.C5, N.E5, N.G5, N.C6, N.B5, N.G5, N.C6, N.REST],
    bass: [N.C3, N.E3, N.G3, N.C4, N.G3, N.E3, N.C3, N.REST],
    leadType: 'square',
    bassType: 'triangle',
  },
};

// ---------------------------------------------------------------------------
// SSR / environment helpers
// ---------------------------------------------------------------------------

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

interface WindowWithLegacyAudio {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
}

function getAudioContextCtor(): typeof AudioContext | null {
  if (!isBrowser()) return null;
  const w = window as unknown as WindowWithLegacyAudio;
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

const STORAGE_KEY = 'surggies:audio';

interface StoredVolumes {
  master?: unknown;
  sfx?: unknown;
  bgm?: unknown;
}

// ---------------------------------------------------------------------------
// AudioEngine
// ---------------------------------------------------------------------------

export class AudioEngine {
  masterVolume = 0.7;
  sfxVolume = 0.8;
  bgmVolume = 0.5;

  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private bgmBus: GainNode | null = null;
  private _initialized = false;
  private current: ActiveBgm | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  /** Queued SFX requested before init() resolves. Played once init completes. */
  private pendingSfx: SfxKey[] = [];
  private pendingBgm: { key: BgmKey; crossfadeMs?: number } | null = null;

  constructor() {
    // Load persisted volumes synchronously so even pre-init reads are correct.
    this.loadFromStorage();
  }

  get initialized(): boolean {
    return this._initialized;
  }

  async init(): Promise<void> {
    if (this._initialized) return;
    const Ctor = getAudioContextCtor();
    if (!Ctor) return; // SSR or unsupported browser — stay no-op.

    try {
      const ctx = new Ctor();
      // Browsers may start suspended until a user gesture finishes propagating.
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch {
          // Ignore — we'll still play once it resumes naturally.
        }
      }

      const master = ctx.createGain();
      master.gain.value = this.masterVolume;
      master.connect(ctx.destination);

      const sfxBus = ctx.createGain();
      sfxBus.gain.value = this.sfxVolume;
      sfxBus.connect(master);

      const bgmBus = ctx.createGain();
      bgmBus.gain.value = this.bgmVolume;
      bgmBus.connect(master);

      this.ctx = ctx;
      this.masterGain = master;
      this.sfxBus = sfxBus;
      this.bgmBus = bgmBus;
      this.noiseBuffer = this.buildNoiseBuffer(ctx);
      this._initialized = true;

      // Expose globally for non-imported consumers (other agents read this).
      try {
        (window as unknown as { surggiesAudio?: AudioEngine }).surggiesAudio = this;
      } catch {
        // Ignore — non-fatal.
      }

      // Drain any queued requests.
      const queued = this.pendingSfx.splice(0);
      for (const k of queued) this.playSfx(k);
      if (this.pendingBgm) {
        const pb = this.pendingBgm;
        this.pendingBgm = null;
        this.playBgm(pb.key, pb.crossfadeMs);
      }
    } catch {
      // Initialization failed — engine stays in no-op state.
      this._initialized = false;
    }
  }

  // -------------------------------------------------------------------------
  // SFX
  // -------------------------------------------------------------------------

  playSfx(key: SfxKey): void {
    if (!isBrowser()) return;
    if (!this._initialized || !this.ctx || !this.sfxBus) {
      // Queue a few — but cap to avoid unbounded growth before init.
      if (this.pendingSfx.length < 16) this.pendingSfx.push(key);
      return;
    }

    const ctx = this.ctx;
    const bus = this.sfxBus;

    // Duck the music briefly so the SFX cuts through the mix.
    this.duckBgm();

    // Per-trigger jitter so back-to-back repeats (e.g. rapid hits) don't sound
    // mechanically identical. A small pitch ratio + gain wobble is enough.
    const pitch = 1 + (Math.random() - 0.5) * 0.06; // ±3%
    const gainJitter = 1 + (Math.random() - 0.5) * 0.16; // ±8%

    // Noise-based SFX get special treatment.
    const noise = NOISE_SFX[key];
    if (noise) {
      this.playNoise(ctx, bus, noise.dur, noise.gain * gainJitter, pitch);
      return;
    }

    const steps = SFX_DEFS[key];
    if (!steps || steps.length === 0) return;

    let t = ctx.currentTime;
    for (const step of steps) {
      if (step.freq <= 0 || (step.gain ?? 0) <= 0) {
        t += step.dur;
        continue;
      }
      this.playTone(ctx, bus, step, t, pitch, gainJitter);
      t += step.dur;
    }
  }

  /**
   * Briefly dip the BGM bus (~4dB ≈ ×0.63) for ~250ms so a triggered SFX
   * punches through, then ramp back to the user's bgm volume. Cheap: just two
   * scheduled ramps on the existing bgmBus gain. No-op if BGM isn't running.
   */
  private duckBgm(): void {
    if (!this.ctx || !this.bgmBus || !this.current) return;
    const now = this.ctx.currentTime;
    const target = this.bgmVolume;
    const ducked = target * 0.63; // ~ -4 dB
    try {
      const g = this.bgmBus.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(Math.max(0.0001, g.value), now);
      g.linearRampToValueAtTime(ducked, now + 0.03);
      g.linearRampToValueAtTime(target, now + 0.25);
    } catch {
      // Ignore — ducking is purely cosmetic.
    }
  }

  private playTone(
    ctx: AudioContext,
    dest: AudioNode,
    step: ToneStep,
    when: number,
    pitch = 1,
    gainMul = 1,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = step.type ?? 'square';
    osc.frequency.setValueAtTime(step.freq * pitch, when);
    if (step.sweepTo && step.sweepTo > 0) {
      osc.frequency.linearRampToValueAtTime(step.sweepTo * pitch, when + step.dur);
    }

    const g = ctx.createGain();
    const peak = (step.gain ?? 0.3) * gainMul;
    // Quick attack, exponential decay for a chiptune feel.
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(peak, when + Math.min(0.005, step.dur * 0.1));
    g.gain.exponentialRampToValueAtTime(
      Math.max(0.0001, peak * 0.01),
      when + step.dur,
    );

    osc.connect(g);
    g.connect(dest);
    osc.start(when);
    osc.stop(when + step.dur + 0.02);
  }

  private buildNoiseBuffer(ctx: AudioContext): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * 0.5); // 0.5s of noise to slice from
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buf;
  }

  private playNoise(
    ctx: AudioContext,
    dest: AudioNode,
    dur: number,
    peak: number,
    pitch = 1,
  ): void {
    if (!this.noiseBuffer) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    // Vary playback rate slightly so repeated hits don't sound identical.
    src.playbackRate.value = pitch;
    // Add a lowpass-ish character via a biquad filter for a punchier hit.
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1200 * pitch;
    filter.Q.value = 0.8;

    const g = ctx.createGain();
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(peak, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    src.connect(filter);
    filter.connect(g);
    g.connect(dest);
    src.start(now);
    src.stop(now + dur + 0.02);
  }

  // -------------------------------------------------------------------------
  // BGM
  // -------------------------------------------------------------------------

  playBgm(key: BgmKey, crossfadeMs = 600): void {
    if (!isBrowser()) return;
    if (!this._initialized || !this.ctx || !this.bgmBus) {
      this.pendingBgm = { key, crossfadeMs };
      return;
    }
    if (this.current && this.current.key === key) return; // no-op same track

    const ctx = this.ctx;
    const track = BGM_DEFS[key];
    if (!track) return;

    // Fade out previous, fade in new.
    const fadeS = Math.max(0, crossfadeMs) / 1000;
    const now = ctx.currentTime;

    const prev = this.current;
    if (prev) {
      try {
        prev.gain.gain.cancelScheduledValues(now);
        prev.gain.gain.setValueAtTime(prev.gain.gain.value, now);
        prev.gain.gain.linearRampToValueAtTime(0, now + fadeS);
      } catch {
        // Ignore.
      }
      // Stop the scheduler shortly after the fade so it doesn't keep emitting notes.
      const oldTimer = prev.timer;
      window.setTimeout(() => {
        window.clearInterval(oldTimer);
        try {
          prev.gain.disconnect();
        } catch {
          // Ignore.
        }
      }, crossfadeMs + 50);
    }

    // New BGM bus gain (per-track) feeds the global bgmBus.
    const trackGain = ctx.createGain();
    trackGain.gain.setValueAtTime(0, now);
    trackGain.gain.linearRampToValueAtTime(1, now + Math.max(0.01, fadeS));
    trackGain.connect(this.bgmBus);

    const loopDur = track.step * track.lead.length;
    // Schedule one full loop immediately, then keep scheduling ahead of time.
    const scheduleLoop = (startAt: number): void => {
      for (let i = 0; i < track.lead.length; i++) {
        const when = startAt + i * track.step;
        const leadFreq = track.lead[i];
        const bassFreq = track.bass[i];
        const arpFreq = track.arp ? track.arp[i] : 0;

        if (leadFreq > 0) {
          this.playTone(
            ctx,
            trackGain,
            {
              freq: leadFreq,
              dur: track.step * 0.95,
              type: track.leadType ?? 'square',
              gain: 0.18,
            },
            when,
          );
        }
        if (bassFreq > 0) {
          this.playTone(
            ctx,
            trackGain,
            {
              freq: bassFreq,
              dur: track.step * 0.95,
              type: track.bassType ?? 'triangle',
              gain: 0.22,
            },
            when,
          );
        }
        if (arpFreq && arpFreq > 0) {
          this.playTone(
            ctx,
            trackGain,
            {
              freq: arpFreq,
              dur: track.step * 0.6,
              type: track.arpType ?? 'square',
              gain: 0.12,
            },
            when,
          );
        }
      }
    };

    // Kick off first loop now.
    let nextLoopAt = now;
    scheduleLoop(nextLoopAt);
    nextLoopAt += loopDur;

    // Re-schedule the next loop a little before it's due.
    const timer = window.setInterval(() => {
      if (!this.ctx) return;
      // If we've fallen behind (tab throttled), resync to current time.
      const lookahead = 0.25;
      while (nextLoopAt < this.ctx.currentTime + lookahead) {
        scheduleLoop(nextLoopAt);
        nextLoopAt += loopDur;
      }
    }, Math.max(50, (loopDur * 1000) / 2));

    this.current = { key, gain: trackGain, timer };
  }

  stopBgm(crossfadeMs = 400): void {
    if (!isBrowser()) return;
    this.pendingBgm = null;
    if (!this._initialized || !this.ctx || !this.current) return;
    const ctx = this.ctx;
    const cur = this.current;
    const now = ctx.currentTime;
    const fadeS = Math.max(0, crossfadeMs) / 1000;
    try {
      cur.gain.gain.cancelScheduledValues(now);
      cur.gain.gain.setValueAtTime(cur.gain.gain.value, now);
      cur.gain.gain.linearRampToValueAtTime(0, now + fadeS);
    } catch {
      // Ignore.
    }
    const timer = cur.timer;
    window.setTimeout(() => {
      window.clearInterval(timer);
      try {
        cur.gain.disconnect();
      } catch {
        // Ignore.
      }
    }, crossfadeMs + 50);
    this.current = null;
  }

  // -------------------------------------------------------------------------
  // Settings
  // -------------------------------------------------------------------------

  setVolumes(opts: { master?: number; sfx?: number; bgm?: number }): void {
    if (opts.master !== undefined) this.masterVolume = clamp01(opts.master);
    if (opts.sfx !== undefined) this.sfxVolume = clamp01(opts.sfx);
    if (opts.bgm !== undefined) this.bgmVolume = clamp01(opts.bgm);

    if (this.ctx && this.masterGain && this.sfxBus && this.bgmBus) {
      const now = this.ctx.currentTime;
      this.masterGain.gain.setTargetAtTime(this.masterVolume, now, 0.02);
      this.sfxBus.gain.setTargetAtTime(this.sfxVolume, now, 0.02);
      this.bgmBus.gain.setTargetAtTime(this.bgmVolume, now, 0.02);
    }

    this.saveToStorage();
  }

  loadFromStorage(): void {
    if (!isBrowser()) return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      const obj = parsed as StoredVolumes;
      if (typeof obj.master === 'number') this.masterVolume = clamp01(obj.master);
      if (typeof obj.sfx === 'number') this.sfxVolume = clamp01(obj.sfx);
      if (typeof obj.bgm === 'number') this.bgmVolume = clamp01(obj.bgm);
    } catch {
      // Ignore — storage may be unavailable (private mode, etc.).
    }
  }

  saveToStorage(): void {
    if (!isBrowser()) return;
    try {
      const payload: StoredVolumes = {
        master: this.masterVolume,
        sfx: this.sfxVolume,
        bgm: this.bgmVolume,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore.
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton + safe convenience wrappers
// ---------------------------------------------------------------------------

export const audio: AudioEngine = new AudioEngine();

/** Safe to call before init — queues until ready, no-ops on SSR. */
export function playSfx(key: SfxKey): void {
  audio.playSfx(key);
}

/** Safe to call before init — queues until ready, no-ops on SSR. */
export function playBgm(key: BgmKey, crossfadeMs?: number): void {
  audio.playBgm(key, crossfadeMs);
}
